// Chrome: sidebar + topbar — integrated with React Router + AuthContext
import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { FRONTEND_VERSION, BUILD_DATE } from '../version'
import api from '../api/axios'
import Icon from './icons'
import { useContent } from '../content/ContentProvider'

// Map our app routes → design nav ids
const ADMIN_NAV = (t) => [
  { id: '/',              icon: 'home',    label: t('nav.dashboard') },
  { id: '/companies',     icon: 'star',    label: t('nav.companies') },
  { id: '/partnerships',  icon: 'pin',     label: t('nav.partnerships') },
  { id: '/transactions',  icon: 'history', label: t('nav.transactions') },
  { id: '/reports',       icon: 'book',    label: t('nav.reports') },
  { id: '/users',         icon: 'upload',  label: t('nav.users') },
  { id: '/analytics',     icon: 'info',    label: t('nav.analytics') },
]

const initials = (user) => {
  if (!user) return 'NG'
  const a = (user.firstName || user.username || '?')[0] || '?'
  const b = (user.lastName || '')[0] || ''
  return (a + b).toUpperCase()
}

const fullName = (user) =>
  user ? (user.firstName || user.lastName ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : user.username) : ''

export const NgrsSidebar = () => {
  const { t } = useContent()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()

  const role = (user?.role || 'ADMIN').toUpperCase()
  const isOp = role === 'ADMIN'
  const nav = ADMIN_NAV(t)
  const activeId = location.pathname

  return (
    <aside className="sidebar">
      <div className="sidebar__brand">
        <img src="/assets/logo-hitrail.png" alt={t('nav.brand.alt')} />
        <div className="sidebar__brand-name">
          <span className="sidebar__brand-sub">{t('nav.brand.sub')}</span>
          <span className="sidebar__brand-tag">{t('nav.brand.tag')}</span>
        </div>
      </div>

      <div className="sidebar__product" title={t('nav.product.title')}>
        <div className="sidebar__product-icon">{t('nav.product.abbr')}</div>
        <div className="sidebar__product-meta">
          <div className="sidebar__product-name">{t('nav.product.name')}</div>
          <div className="sidebar__product-sub">{isOp ? t('nav.product.subOperator') : t('nav.product.subCarrier')}</div>
        </div>
        <span style={{ color: 'var(--text-dim)' }}>{Icon.chevronDown({ s: 12 })}</span>
      </div>

      <nav className="sidebar__nav thin-scroll">
        <div className="sidebar__group-title">{isOp ? t('nav.group.operations') : t('nav.group.workspace')}</div>
        {nav.map((it) => (
          <button
            key={it.id}
            type="button"
            className={'nav-item ' + (activeId === it.id ? 'is-active' : '')}
            onClick={() => navigate(it.id)}
          >
            <span className="nav-item__icon">{Icon[it.icon]({ s: 15 })}</span>
            <span>{it.label}</span>
            {it.count && <span className="nav-item__count">{it.count}</span>}
          </button>
        ))}

        <div className="sidebar__group-title">{t('nav.group.account')}</div>
        <button type="button" className="nav-item">
          <span className="nav-item__icon">{Icon.settings({ s: 14 })}</span>
          <span>{t('nav.settings')}</span>
        </button>
        <button type="button" className="nav-item">
          <span className="nav-item__icon">{Icon.book({ s: 14 })}</span>
          <span>{t('nav.apiReference')}</span>
          <span style={{ marginLeft: 'auto', color: 'var(--text-dim)' }}>{Icon.external({ s: 11 })}</span>
        </button>
      </nav>

      <div className="sidebar__foot">
        <div className={'ngrs-avatar ' + (isOp ? '' : 'ngrs-avatar--gold')}>{initials(user)}</div>
        <div className="sidebar__foot-meta">
          <div className="sidebar__foot-name">{fullName(user)}</div>
          <div className="sidebar__foot-role">
            {(user?.companyName || 'HiTrail') + (isOp ? t('nav.foot.operatorSuffix') : t('nav.foot.carrierSuffix'))}
          </div>
        </div>
        <button className="iconbtn" style={{ width: 28, height: 28 }} onClick={logout} title={t('nav.signOut')} aria-label={t('nav.signOut')}>
          {Icon.logout({ s: 13 })}
        </button>
      </div>

      <VersionBadge />
    </aside>
  )
}

function VersionBadge() {
  const { t } = useContent()
  const [be, setBe] = useState(null)
  useEffect(() => {
    api.get('/api/admin/analytics/system').then((r) => {
      setBe(r.data)
    }).catch(() => {})
  }, [])
  return (
    <div
      style={{
        padding: '8px 14px 12px',
        borderTop: '1px solid var(--border-soft)',
        fontSize: 'var(--fs-eyebrow)',
        color: 'var(--text-dim)',
        fontFamily: 'var(--font-mono)',
        lineHeight: 1.5,
      }}
      title={t('nav.version.title')}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>{t('nav.version.fe')}</span>
        <span style={{ color: 'var(--text-muted)' }}>{FRONTEND_VERSION}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>{t('nav.version.be')}</span>
        <span style={{ color: 'var(--text-muted)' }}>{be ? t('nav.version.uptimePrefix') + be.uptime?.human : '—'}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', opacity: 0.6 }}>
        <span>{t('nav.version.build')}</span>
        <span>{BUILD_DATE}</span>
      </div>
    </div>
  )
}

export const NgrsTopbar = ({ crumb = '' }) => {
  const { t } = useContent()
  const { user } = useAuth()
  const role = (user?.role || 'ADMIN').toUpperCase()
  const isOp = role === 'ADMIN'
  // Detect env from hostname; default to LOCAL for dev
  const host = typeof window !== 'undefined' ? window.location.hostname : ''
  const env =
    host.includes('prod') ? 'PRD' :
    host.includes('localhost') ? 'LOCAL' :
    host.includes('hitrail.eu') ? 'ACC' : 'DEV'

  return (
    <header className="topbar">
      <div className="topbar__crumb">
        <span>{isOp ? t('nav.topbar.operator') : t('nav.topbar.carrier')}</span>
        <span className="topbar__sep">/</span>
        <b>{crumb}</b>
      </div>
      <span className={'role-badge ' + (isOp ? 'role-badge--operator' : 'role-badge--carrier')}>
        {Icon.dot({ s: 5 })}
        {isOp ? t('nav.topbar.operator') : t('nav.topbar.carrier')}
      </span>
      <span className={'env-tag ' + (env === 'PRD' ? 'env-tag--prd' : '')}>{env}</span>

      <div className="topbar__search">
        {Icon.search({ s: 14 })}
        <input placeholder={isOp ? t('nav.search.placeholderOperator') : t('nav.search.placeholderCarrier')} />
        <kbd>⌘K</kbd>
      </div>

      {!isOp && user?.companyName && (
        <div className="topbar__company">
          <div className="topbar__company-logo">{user.companyName.substring(0, 2).toUpperCase()}</div>
          <div>
            <div className="topbar__company-name">{user.companyName}</div>
            <div className="topbar__company-rics">
              {t('nav.topbar.companyRics', { rics: user.companyRics || '----', country: user.country || '--' })}
            </div>
          </div>
          {Icon.chevronDown({ s: 12 })}
        </div>
      )}

      <button className="iconbtn" title={t('nav.topbar.notifications')} aria-label={t('nav.topbar.notifications')}>
        <span className="dot" />
        {Icon.alert({ s: 16 })}
      </button>
      <button className="iconbtn" title={t('nav.topbar.settings')} aria-label={t('nav.topbar.settings')}>
        {Icon.settings({ s: 16 })}
      </button>
    </header>
  )
}

// Drop-in shell wrapper — passes through children
export const NgrsShell = ({ crumb, children }) => (
  <div className="app">
    <NgrsSidebar />
    <div className="main">
      <NgrsTopbar crumb={crumb} />
      {children}
    </div>
  </div>
)
